// Essai + derniers envois: a sample SMS to the team's own phone (5 per day),
// then the latest SMS with their outcome (number masked).

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, MessageSquareDashed, Send } from 'lucide-react';
import {
  Badge, Banner, Card, EmptyState, Field, Segmented,
} from '../../design/ui';
import { EVENTS, fail, formatWhen, post } from './api';

const STATUS_TONE = { sent: 'success', scheduled: 'info', failed: 'danger', skipped: 'warning' };
const STATUS_ORDER = ['sent', 'scheduled', 'failed', 'skipped', 'sending'];

const statusLabel = (item, lang, t) => {
  if (item.status === 'scheduled' && item.scheduled_for) {
    const when = formatWhen(item.scheduled_for, lang);
    return t('customerSms.history.scheduledAt', 'Programmé · {{when}}', { when });
  }
  if (item.status === 'skipped' && item.error === 'platform_sender_inactive') {
    return t('customerSms.history.senderInactive', 'Non envoyé · expéditeur inactif');
  }
  return t(`customerSms.history.status.${item.status}`, item.status);
};

// Counts per status among the messages shown (the latest 30).
export const statusCounts = (messages) => messages.reduce((acc, item) => ({ ...acc, [item.status]: (acc[item.status] || 0) + 1 }), {});

export const TestCard = ({ view, onSent }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [event, setEvent] = useState('parcel_problem');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const sendTest = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const res = await post('/test', { phone, event });
      setResult({ tone: 'success', text: t('customerSms.test.sent', 'SMS d’essai envoyé au {{to}} sous « {{sender}} ».', { to: res.data.to, sender: res.data.sender }) });
      onSent();
    } catch (err) {
      setResult({ tone: 'danger', text: fail(err, t('customerSms.test.error', "L'essai n'a pas pu être envoyé.")) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      id="sms-test"
      icon={Send}
      title={t('customerSms.test.title', 'Tester')}
      description={t('customerSms.test.subtitle', 'Recevez une notification sur votre propre mobile, avec les textes enregistrés.')}
    >
      <form className="u-form" onSubmit={sendTest}>
        <div className="sms-v2__test">
          <Field label={t('customerSms.test.phone', 'Votre mobile')} htmlFor="sms-test-phone">
            <input id="sms-test-phone" className="u-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="06 12 34 56 78" maxLength={32} />
          </Field>
          <Field label={t('customerSms.test.event', 'Notification')} htmlFor="sms-test-event">
            <select id="sms-test-event" className="u-select" value={event} onChange={(e) => setEvent(e.target.value)}>
              {EVENTS.map((ev) => <option key={ev} value={ev}>{t(`customerSms.events.${ev}.title`, ev)}</option>)}
            </select>
          </Field>
          <button type="submit" className="u-btn u-btn--secondary" disabled={busy || phone.trim().length < 6 || !view.ovh_configured}>
            <Send aria-hidden="true" />
            {busy ? t('customerSms.test.sending', 'Envoi…') : t('customerSms.test.send', "Envoyer l'essai")}
          </button>
        </div>
        <p className="u-help">
          {t('customerSms.test.limit', '{{n}} essais par jour. Le numéro n’est pas conservé (seuls ses 2 derniers chiffres apparaissent ci-dessous).', { n: view.limits?.test_per_day || 5 })}
        </p>
        {result && <Banner tone={result.tone}>{result.text}</Banner>}
      </form>
    </Card>
  );
};

export const HistoryCard = ({ messages, lang, filter, onFilter }) => {
  const { t } = useTranslation();
  const counts = useMemo(() => statusCounts(messages), [messages]);
  const present = STATUS_ORDER.filter((status) => counts[status]);
  const shown = filter === 'all' ? messages : messages.filter((item) => item.status === filter);

  return (
    <Card
      id="sms-history"
      icon={History}
      title={t('customerSms.history.title', 'Derniers envois')}
      description={t('customerSms.history.subtitle', 'Les 30 derniers SMS de votre compte, conservés 180 jours.')}
      flush
    >
      {messages.length === 0 ? (
        <EmptyState icon={MessageSquareDashed} title={t('customerSms.history.empty', 'Aucun SMS envoyé pour le moment.')}>
          {t('channelsUi.sms.historyEmpty', 'Les SMS apparaissent ici dès qu’une notification activée part, avec leur résultat.')}
        </EmptyState>
      ) : (
        <>
          {present.length > 1 && (
            <div className="u-toolbar">
              <Segmented
                label={t('channelsUi.sms.historyFilter', 'Filtrer par résultat')}
                value={filter}
                onChange={onFilter}
                options={[
                  { value: 'all', label: t('channelsUi.sms.all', 'Tous'), count: messages.length },
                  ...present.map((status) => ({ value: status, label: t(`customerSms.history.status.${status}`, status), count: counts[status] })),
                ]}
              />
            </div>
          )}
          <div className="u-table-scroll">
            <table className="u-table sms-v2__table">
              <thead>
                <tr>
                  <th style={{ width: '13%' }}>{t('customerSms.history.date', 'Date')}</th>
                  <th style={{ width: '19%' }}>{t('customerSms.history.event', 'Notification')}</th>
                  <th style={{ width: '17%' }}>{t('customerSms.history.parcel', 'Colis')}</th>
                  <th style={{ width: '14%' }}>{t('customerSms.history.to', 'Destinataire')}</th>
                  <th style={{ width: '11%' }}>{t('customerSms.history.sender', 'Expéditeur')}</th>
                  <th style={{ width: '26%' }}>{t('customerSms.history.statusCol', 'Statut')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => {
                  const eventLabel = item.event === 'test'
                    ? `${t('customerSms.history.test', 'Essai')} · ${t(`customerSms.events.${item.test_of}.title`, item.test_of || '')}`
                    : t(`customerSms.events.${item.event}.title`, item.event);
                  const label = statusLabel(item, lang, t);
                  return (
                    <tr key={item.id}>
                      <td className="u-num" title={formatWhen(item.created_at, lang)}>{formatWhen(item.created_at, lang)}</td>
                      <td title={eventLabel}>{eventLabel}</td>
                      <td>{item.tracking_number ? <span className="u-code" title={item.tracking_number}>{item.tracking_number}</span> : '—'}</td>
                      <td className="u-num">{item.to}</td>
                      <td title={item.sender}>{item.sender}</td>
                      <td>
                        <span title={item.status === 'failed' ? item.error || '' : label}>
                          <Badge tone={STATUS_TONE[item.status]} dot={Boolean(STATUS_TONE[item.status])}>{label}</Badge>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};
